import type { AddressDetails } from '@/api/workflows';

/**
 * Flatten a Canada Post `Retrieve` result into the single address line an
 * `addressRole: 'full'` field holds.
 *
 * Shape: `232-46 Glamis Green SW, Calgary, AB T3E 6V3` — unit prefixed to the
 * street line with a hyphen (Canada Post's own convention, and the inverse of
 * what the BE does when it strips `{subBuilding}-` off `line1` so the street
 * and apartment slots of a compound don't duplicate the unit), then city, then
 * province code and postal code as one trailing group.
 *
 * `line2` carries the extra line Canada Post returns for rural / military /
 * commercial addresses (e.g. "RR 2 STN Main"). That is a line in its own right,
 * not a unit, so it gets its own comma segment — only `subBuilding` hyphenates
 * onto the street.
 *
 * Country is deliberately left out: the lookup is Canada-only ('CAN'), so
 * printing "Canada" on every line would be noise.
 *
 * Empty segments drop out, so a partial Retrieve (no postal code, say) still
 * produces a clean line instead of dangling commas.
 */
export function formatFullAddress(details: AddressDetails): string {
  const streetLine = formatUnitAndStreet(details);
  // Province + postal read as one unit in Canadian addressing ("AB T3E 6V3"),
  // so they join on a space and only then become a comma segment.
  const region = [details.provinceCode?.trim(), details.postalCode?.trim()]
    .filter((p) => !!p)
    .join(' ');
  return [streetLine, details.line2?.trim(), details.city?.trim(), region]
    .filter((p) => !!p)
    .join(', ');
}

/** `232-46 Glamis Green SW` — the unit hyphenated onto the street, Canada Post's convention. */
function formatUnitAndStreet(details: AddressDetails): string {
  const unit = details.subBuilding?.trim() ?? '';
  const street = details.line1?.trim() ?? '';
  return unit && street ? `${unit}-${street}` : street || unit;
}

/**
 * The street line for an address block that has NO apartment field to take the
 * unit — the RDSP relinquishing-issuer block, for one. The BE strips the unit
 * off `line1` so a compound with an apartment slot doesn't print it twice; a
 * block without one would otherwise drop it ("1-100 King St W" arriving as
 * "100 King St W"). So the unit goes back on the street, and `line2` (the
 * commercial / rural extra line, which would have gone to the apartment slot
 * too) follows it.
 */
export function formatStreetLineWithoutApartment(details: AddressDetails): string {
  return [formatUnitAndStreet(details), details.line2?.trim()].filter((p) => !!p).join(', ');
}
